import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Callout } from '@/components/ui/Callout'
import { ReferenceError } from '@/lib/reference'
import { REFERENCE_ERROR_MESSAGE } from '@/lib/reference/reference-context'

export type FieldValues = Record<string, string>

export interface FormField {
  key: string
  header: string
  editor: 'text' | 'number' | 'select' | 'readonly'
  options?: string[]
  /** Natural key of an existing row: shown, never editable. */
  frozen?: boolean
  /**
   * Hard cap on what can be typed, for a field with a real length limit.
   *
   * Enforced at the input rather than on save: a group code may be seven
   * characters, and silently keeping the first seven of a longer one would
   * create a group nobody named.
   */
  maxLength?: number
}

/** Beyond this many choices a dropdown is unusable; type-ahead takes over. */
const DATALIST_THRESHOLD = 40

/** Add or edit one reference row. Errors come back from the provider by field. */
export function RowFormDialog({
  title,
  columns,
  initial,
  onCancel,
  onSubmit,
}: {
  title: string
  columns: FormField[]
  initial: FieldValues
  onCancel: () => void
  onSubmit: (values: FieldValues) => Promise<void>
}) {
  const [values, setValues] = useState<FieldValues>(() =>
    Object.fromEntries(columns.map((column) => [column.key, initial[column.key] ?? ''])),
  )
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setFieldError(null)
    setFormError(null)
    try {
      await onSubmit(values)
    } catch (error) {
      if (error instanceof ReferenceError && error.field) {
        setFieldError({ field: error.field, message: REFERENCE_ERROR_MESSAGE[error.code] })
      } else if (error instanceof ReferenceError) {
        setFormError(REFERENCE_ERROR_MESSAGE[error.code])
      } else {
        setFormError(REFERENCE_ERROR_MESSAGE.unknown)
      }
      setSaving(false)
    }
  }

  return (
    <Modal title={title} onClose={onCancel}>
      <form onSubmit={handleSubmit} className="mt-6">
        {formError && (
          <Callout tone="error" className="mb-5">
            {formError}
          </Callout>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {columns.map((column) => (
            <Field
              key={column.key}
              field={column}
              value={values[column.key] ?? ''}
              error={fieldError?.field === column.key ? fieldError.message : undefined}
              onChange={(next) => setValues((current) => ({ ...current, [column.key]: next }))}
            />
          ))}
        </div>

        <div className="mt-8 flex gap-3">
          <Button type="submit" loading={saving} fullWidth>
            Save
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} fullWidth>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function Field({
  field,
  value,
  error,
  onChange,
}: {
  field: FormField
  value: string
  error?: string
  onChange: (value: string) => void
}) {
  const id = useId()
  const errorId = `${id}-error`
  const options = field.options ?? []
  const useDatalist = field.editor === 'select' && options.length > DATALIST_THRESHOLD

  const shell = [
    't-body-md h-12 w-full rounded-[12px] bg-canvas px-4 text-ink outline-none',
    'transition-[border-color] duration-150',
    error ? 'border-2 border-semantic-down px-[15px]' : 'border border-hairline focus:border-2 focus:border-primary focus:px-[15px]',
    'disabled:bg-surface-soft disabled:text-muted disabled:cursor-not-allowed',
  ].join(' ')

  return (
    <div>
      <label htmlFor={id} className="t-title-sm mb-2 block text-ink">
        {field.header}
      </label>

      {field.editor === 'select' && !useDatalist ? (
        <select
          id={id}
          value={value}
          disabled={field.frozen}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => onChange(event.target.value)}
          className={shell}
        >
          <option value="" />
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <>
          <input
            id={id}
            type={field.editor === 'number' ? 'number' : 'text'}
            value={value}
            maxLength={field.maxLength}
            disabled={field.frozen || field.editor === 'readonly'}
            list={useDatalist ? `${id}-options` : undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => onChange(event.target.value)}
            className={shell}
          />
          {useDatalist && (
            <datalist id={`${id}-options`}>
              {options.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          )}
        </>
      )}

      {error && (
        <p id={errorId} role="alert" className="t-caption mt-2 text-semantic-down">
          {error}
        </p>
      )}
    </div>
  )
}
